import { useEffect, useRef } from 'react'
import { useAccount } from 'wagmi'

import { getActiveChainId } from '../config/active'
import { ensureAppChainInWallet } from '../domain/wallet/ensureAppChainInWallet'
import { getConnectorChainId } from '../domain/wallet/getConnectorChainId'

const CHAIN_ENSURED_SESSION_KEY = 'nm.walletChainEnsuredFor'

function readEnsuredKey(): string | null {
  if (typeof window === 'undefined') return null
  try {
    return window.sessionStorage.getItem(CHAIN_ENSURED_SESSION_KEY)
  } catch {
    return null
  }
}

function markChainEnsured(key: string): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(CHAIN_ENSURED_SESSION_KEY, key)
  } catch {
    /* quota / private mode */
  }
}

function buildEnsuredKey(address: string, chainId: number): string {
  return `${address.toLowerCase()}:${chainId}`
}

/**
 * Tras conectar la wallet, pide a MetaMask añadir/cambiar a la cadena del build.
 * Los saldos de la app se leen por RPC propio; la wallet necesita la red para firmar.
 * Una vez por cuenta+cadena en la pestaña (no por uid de conector ni cada visibility).
 */
export default function EnsureWalletChainGuard() {
  const { address, connector, isConnected, status } = useAccount()
  const appChainId = getActiveChainId()
  const ensuredForKey = useRef<string | null>(readEnsuredKey())

  useEffect(() => {
    if (!isConnected || status !== 'connected' || !connector || !address) return

    const ensuredKey = buildEnsuredKey(address, appChainId)
    if (ensuredForKey.current === ensuredKey) return

    let cancelled = false

    void (async () => {
      try {
        const walletChainId = await getConnectorChainId(connector)
        if (walletChainId === appChainId) {
          if (!cancelled) {
            ensuredForKey.current = ensuredKey
            markChainEnsured(ensuredKey)
          }
          return
        }

        await ensureAppChainInWallet(connector)
        if (!cancelled) {
          ensuredForKey.current = ensuredKey
          markChainEnsured(ensuredKey)
        }
      } catch {
        /* el usuario puede rechazar; se reintentará al firmar */
      }
    })()

    return () => {
      cancelled = true
    }
  }, [address, appChainId, connector, isConnected, status])

  return null
}
